import React, { useEffect, useRef, useState } from "react";
import "./panda-follower.css";

function dist(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
}

export default function RobotFollower() {
  const ref = useRef(null);
  const current = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const target = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const anim = useRef(null);
  const lastTime = useRef(null);

  const [walking, setWalking] = useState(false);
  const [facingLeft, setFacingLeft] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const SPEED = 200;      // smooth speed
    const THRESHOLD = 8;   // stop distance

    function step(t) {
      if (!lastTime.current) lastTime.current = t;
      const dt = Math.min(40, t - lastTime.current) / 1000;
      lastTime.current = t;

      const c = current.current;
      const trg = target.current;
      const d = dist(c, trg);

      // When robot reaches cursor
      if (d < THRESHOLD) {
        c.x = trg.x;
        c.y = trg.y;

        el.style.transform = `translate(-50%, -50%) translate(${c.x}px, ${c.y}px)`;
        setWalking(false);

        anim.current = null;
        lastTime.current = null;
        return;
      }

      // Move towards cursor
      const vx = (trg.x - c.x) / d;
      const vy = (trg.y - c.y) / d;
      const stepLen = SPEED * dt;

      c.x += vx * stepLen;
      c.y += vy * stepLen;

      el.style.transform = `translate(-50%, -50%) translate(${c.x}px, ${c.y}px)`;

      setWalking(true);
      setFacingLeft(trg.x < c.x);

      anim.current = requestAnimationFrame(step);
    }

    function onMove(e) {
      const clientX = e.clientX ?? (e.touches && e.touches[0]?.clientX);
      const clientY = e.clientY ?? (e.touches && e.touches[0]?.clientY);
      if (typeof clientX !== "number" || typeof clientY !== "number") return;

      target.current.x = clientX;
      target.current.y = clientY;

      if (!anim.current) anim.current = requestAnimationFrame(step);
    }

    function onLeave() {
      setWalking(false);
    }

    window.addEventListener("mousemove", onMove, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("mouseout", onLeave);
    window.addEventListener("mouseleave", onLeave);

    el.style.transform = `translate(-50%, -50%) translate(${current.current.x}px, ${current.current.y}px)`;

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseout", onLeave);
      window.removeEventListener("mouseleave", onLeave);
      if (anim.current) cancelAnimationFrame(anim.current);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`robot-follower ${walking ? "walking" : "idle"} ${
        facingLeft ? "left" : "right"
      }`}
      aria-hidden="true"
      style={{ width: 40, height: 40 }}   // SMALL ROBOT
    >
      {/* Small Robot SVG */}
      <svg width="40" height="40" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        <g className="robot">

          {/* Head */}
          <rect x="22" y="6" width="20" height="16" rx="3" fill="#cfd8dc" stroke="#222" />
          <circle cx="28" cy="14" r="2" fill="#222" />
          <circle cx="36" cy="14" r="2" fill="#222" />

          {/* Antenna */}
          <line x1="32" y1="2" x2="32" y2="6" stroke="#222" strokeWidth="2" />
          <circle cx="32" cy="2" r="2" fill="#ff5252" />

          {/* Body */}
          <rect x="20" y="24" width="24" height="20" rx="3" fill="#90a4ae" stroke="#222" />

          {/* Arms */}
          <g className="arm left-arm">
            <rect x="14" y="26" width="6" height="14" rx="2" fill="#607d8b" />
          </g>

          <g className="arm right-arm">
            <rect x="44" y="26" width="6" height="14" rx="2" fill="#607d8b" />
          </g>

          {/* Legs */}
          <g className="leg left-leg">
            <rect x="24" y="44" width="6" height="14" rx="2" fill="#37474f" />
          </g>

          <g className="leg right-leg">
            <rect x="34" y="44" width="6" height="14" rx="2" fill="#37474f" />
          </g>

        </g>
      </svg>
    </div>
  );
}
